import { Injectable } from '@angular/core';
import {
  ListThemeLineItemTypesRequest,
  ListThemeLineItemTypesResponse,
  SearchThemeReplacementTagsRequest,
  SearchThemeReplacementTagsResponse,
  ListThemesRequest,
  ListThemesResponse,
  SituViewWebApiClient,
  Theme,
  ThemeLineItemType,
  ScoredThemeReplacementTag
} from 'situview-webapi-client';
import { BuildingMaterialTags } from '../shared/models/building-material-tags';
import { FurnishingTags } from '../shared/models/furnishing-tags';
import { HomeTags } from '../shared/models/home-tags';
import { StaircaseTags } from '../shared/models/staircase-tags';
import { WindowTags } from '../shared/models/window-tags';

export interface ICache<T> {
  timestamp: number;
  companyId?: string;
  subjectId?: string;
  calculateThemeCompletion?: boolean;
  data: T[];
}

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private readonly fiveMinuteCacheDuration = 5 * 60 * 1000; // 5 minutes in milliseconds
  private readonly thirtyMinuteCacheDuration = 30 * 60 * 1000; // 30 minutes in milliseconds

  constructor(
    private situViewClient: SituViewWebApiClient
  ) {}

  private themesCache: ICache<Theme> | undefined;
  private loadingThemesPromise: Promise<Theme[]> | null = null;
  LoadThemes(companyId?: string, calculateThemeCompletion: boolean = false): Promise<Theme[]> {
    if (this.themesCache &&
      companyId === this.themesCache.companyId &&
      (calculateThemeCompletion === this.themesCache.calculateThemeCompletion || this.themesCache.calculateThemeCompletion === true) &&
      (Date.now() - this.themesCache.timestamp) < this.fiveMinuteCacheDuration) {
      return Promise.resolve(this.themesCache.data);
    } else if (this.loadingThemesPromise && companyId === this.themesCache?.companyId) {
      return this.loadingThemesPromise;
    } else {
      let request = new ListThemesRequest();
      request.companyId = companyId;
      request.calculateThemeCompletion = calculateThemeCompletion;
  
      this.loadingThemesPromise = this.situViewClient.listThemes(request)
        .then((response: ListThemesResponse) => {
          if (response.success) {
            this.themesCache = {
              timestamp: Date.now(),
              companyId: companyId,
              calculateThemeCompletion: calculateThemeCompletion,
              data: response.themes ?? []
            };
            return this.themesCache.data;
          } else {
            return [];
          }
        })
        .catch((error: any) => {
          console.error(error);
          throw error; // Rethrow error to be handled by the caller
        })
        .finally(() => {
          this.loadingThemesPromise = null;
        });
      return this.loadingThemesPromise;
    }
  }

  private themeLineItemTypesCache: ICache<ThemeLineItemType> | undefined;
  private loadingThemeLineItemTypesPromise: Promise<ThemeLineItemType[]> | null = null;
  LoadThemeLineItemTypes(): Promise<ThemeLineItemType[]> {
    if (this.themeLineItemTypesCache && (Date.now() - this.themeLineItemTypesCache.timestamp) < this.thirtyMinuteCacheDuration) {
      return Promise.resolve(this.themeLineItemTypesCache.data);
    } else if (this.loadingThemeLineItemTypesPromise) {
      return this.loadingThemeLineItemTypesPromise;
    } else {
      let request = new ListThemeLineItemTypesRequest();
      //request.themeId = themeId; // Uncomment and set this if needed
  
      this.loadingThemeLineItemTypesPromise = this.situViewClient.listThemeLineItemTypes(request)
        .then((response: ListThemeLineItemTypesResponse) => {
          if (response.success && response.themeLineItemTypes) {
            this.themeLineItemTypesCache = {
              timestamp: Date.now(),
              data: response.themeLineItemTypes
            };
            return this.themeLineItemTypesCache.data;
          } else {
            throw new Error(`There was an issue loading theme line item types.`);
          }
        })
        .catch((error: any) => {
          console.error(error);
          throw error; // Rethrow error to be handled by the caller
        })
        .finally(() => {
          this.loadingThemeLineItemTypesPromise = null;
        });
  
      return this.loadingThemeLineItemTypesPromise;
    }
  }

  SearchThemeReplacementTags(companyId: string, options: SearchThemeReplacementTagsOptions = {}): Promise<ScoredThemeReplacementTag[]> {
    let request = new SearchThemeReplacementTagsRequest();
    request.companyId = companyId;
    if (options.themeLineItemTypeId) request.orderThemeLineItemTypeId = options.themeLineItemTypeId;
    if (options.productTypeIds) request.orderProductTypeIds = options.productTypeIds;
    if (options.productSpecificationIds) request.orderProductSpecificationIds = options.productSpecificationIds;

    request.filterActiveReleases = options.filterActiveReleases;
    request.useIfcTags = options.useIfcTags;
    request.tagGroupsFilter = options.tagGroupFilter;
    request.filterByReleaseId = options.filterByReleaseId;
    request.filterByThemeId = options.filterByThemeId;
    request.ifcTagsOnly = options.ifcTagsOnly;
    request.standardTagsOnly = options.standardTagsOnly;
    request.productTypesFilter = options.productTypeFilter;
    request.filterByLatestIfcFiles = options.filterByLatestIfcFiles;
    
    return this.situViewClient.searchThemeReplacementTags(request)
      .then((response: SearchThemeReplacementTagsResponse) => {
        if (response.success && response.themeReplacementTags) {
          return response.themeReplacementTags;
        } else {
          throw new Error(`There was an issue loading theme line item types.`);
        }
      })
      .catch((error: any) => {
        throw error;
      });
  }

  GetThemeTagsByTemplate(template: string, productType?: string): string[] {
    if (!template) return [];
    switch(template.toLowerCase()) {
      case "tilelay":
      case "brickreplacement":
      case "materialreplacement":
        return Object.keys(BuildingMaterialTags).map(key => (BuildingMaterialTags as any)[key]);
      case "productreplacement":
        return Object.keys(FurnishingTags).map(key => (FurnishingTags as any)[key]);
      case "roofreplacement":
        return ["Roof Covering"];
      case "productidentifierreplacement":
        let staircaseTags = Object.keys(StaircaseTags).map(key => (StaircaseTags as any)[key]);
        //let windowTags = Object.keys(WindowTags).filter(x => typeof WindowTags[x as keyof WindowTags] === "string");
        let windowTags = Object.keys(WindowTags).map(key => (WindowTags as any)[key]);
        if (productType) {
          return productType?.toLowerCase() === "window" ? windowTags : staircaseTags; 
        }
        return [...staircaseTags, ...windowTags];
      default:
        return [];
    }
  }
}

export class SearchThemeReplacementTagsOptions {
  themeLineItemTypeId?: string;
  filterActiveReleases?: boolean;
  productTypeIds?: string[];
  productSpecificationIds?: string[];
  useIfcTags?: boolean;
  ifcTagsOnly?: boolean;
  standardTagsOnly?: boolean;
  tagGroupFilter?: string[];
  productTypeFilter?: string[];
  filterByReleaseId?: string;
  filterByThemeId?: string;
  filterByLatestIfcFiles?: boolean
};